const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const Payment = require('@/lib/Payments/Payment');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const TransactionInProgress = require('@/lib/Payments/TransactionInProgress');
const WorldpayTransactionRequestRecord = require('@/lib/PaymentMethods/Worldpay/Records/Request/WorldpayTransactionRequestRecord');
const WorldpayTransactionResult = require('@/lib/PaymentMethods/Worldpay/Constants/WorldpayTransactionResult');
const WorldpayTransactionType = require('@/lib/PaymentMethods/Worldpay/Constants/WorldpayTransactionType');

/**
 * @param $filter
 * @param $interval
 * @param $modal
 * @param $scope
 * @param $timeout
 * @param {CartService} cartService
 * @param {DialogService} dialogService
 * @param {PaymentMethodService} paymentMethodService
 * @param {PaymentState} paymentState
 * @param {VueModalFactory} vueModalFactory
 * @param {WorldpayConfig} worldpayConfigService
 * @param {WorldpayDriver} worldpayDriverService
 */
const WorldpayPaymentCtrl = function (
    $filter,
    $interval,
    $modal,
    $scope,
    $timeout,
    cartService,
    dialogService,
    paymentMethodService,
    paymentState,
    vueModalFactory,
    worldpayConfigService,
    worldpayDriverService
) {
    $scope.paymentMethodScopes[PaymentMethodRef.CARD_WORLDPAY_CHIP_AND_PIN] = $scope;

    const log = logFactory('Worldpay');

    $scope.worldpayConfig = worldpayConfigService;

    // Set standard payment controller things.
    $scope.thisPaymentMethodRef = PaymentMethodRef.CARD_WORLDPAY_CHIP_AND_PIN;
    $scope.thisPaymentMethod = paymentMethodService.getByRef($scope.thisPaymentMethodRef);

    /**
     * A function to execute once we know the IPC application is ready to listen.
     *
     * @type {function|null}
     */
    $scope.onTerminalReady = null;

    /**
     * Is IPC ready to receive a transaction?
     *
     * @type {boolean|null}
     */
    $scope.ready = null;

    /**
     * To ensure the user clicks the Continue button on IPC, we don't dissmiss the payment modal and complete
     * the order until the state returns to ready.
     * When a payment is received this is set to true. If this is true and a ready status is received, the ok()
     * function will be called.
     *
     * @type {boolean}
     */
    let submitOnReady = false;

    /**
     * Has IPC said it was busy during this modal-being-open-session?
     *
     * @type {boolean}
     */
    $scope.hasBeenBusy = false;

    /**
     * When IPC says it is busy (by returning anything other than a response that says non-busy)
     * a 1 second timeout will be started, after which we'll send a record to check if it is still busy
     * then repeat if necessary.
     * This is a handle for that timeout so it can be cancelled if desired.
     *
     * @type {null}
     */
    $scope.checkStatusTimeout = null;
    $scope.clearCheckStatusTimeout = function () {
        $timeout.cancel($scope.checkStatusTimeout);
    };

    /**
     * Start a transaction for the amount given.
     *
     * @param {number} amount
     */
    $scope.beginTransaction = function beginTransaction(amount) {
        $scope.showWindow();

        $timeout(function () {
            $scope.clearCheckStatusTimeout();

            let record = $scope.createTransactionRequestRecord(amount);
            worldpayDriverService.sendRecord(record);
            $scope.transactionRecord = record;
            submitOnReady = false;
            paymentState.setPaymentModalCancelable(false, $scope.thisPaymentMethodRef);

            cartService.setTransactionInProgress(
                new TransactionInProgress(
                    $scope.thisPaymentMethodRef,
                    record.transactionReference,
                    record.transactionAmount
                )
            );
        }, 500);
    };

    $scope.getDisplayAmount = function () {
        if ($scope.transactionRecord) {
            if ($scope.transactionRecord.transactionType === WorldpayTransactionType.RETURNS) {
                return -$scope.transactionRecord.transactionAmount;
            }
            return $scope.transactionRecord.transactionAmount;
        }

        return null;
    };

    /**
     * Start a transaction for the balance remaining in the cart.
     */
    $scope.beginFullTransaction = function beginFullTransaction() {
        $scope.beginTransaction($scope.cart.getRemainingBalance());
    };

    /**
     * Show the partial payment entry modal and start a transaction with that amount.
     */
    $scope.makePartialPayment = function makePartialPayment() {
        $scope.hideWindow();
        $scope.showPartialPaymentModal('make', function (amount) {
            if (amount) {
                $scope.beginTransaction(amount);
            }
        });
    };

    $scope.isCnp = false;
    $scope.toggleCnp = function toggleCnp() {
        if ($scope.isCnp) {
            // No permission check needed to disable.
            $scope.isCnp = false;
        } else {
            $scope.hideWindow();
            $scope.privilegesManager.requirePrivilege(PrivilegeNames.CNP_PAYMENTS).then(
                /**
                 * @param {PrivilegeCheckResult} result
                 */
                function (result) {
                    if (result.granted) {
                        $scope.isCnp = true;
                    }
                }
            );
        }
    };

    /**
     * Send a record to show the IPC window.
     */
    $scope.showWindow = function showWindow() {
        log.debug('Showing IPC window.');
        worldpayDriverService.sendRecord(
            new WorldpayTransactionRequestRecord(
                WorldpayTransactionType.SHOW_IPC_WINDOW
            )
        );
    };

    /**
     * Send a record to hide the IPC window.
     */
    $scope.hideWindow = function hideWindow() {
        log.debug('Hiding IPC window.');
        worldpayDriverService.sendRecord(
            new WorldpayTransactionRequestRecord(
                WorldpayTransactionType.HIDE_IPC_WINDOW
            )
        );
    };

    /**
     * Send a record to return the busy/not busy status of IPC.
     */
    $scope.checkStatus = function checkStatus() {
        log.debug('Checking IPC status.');
        $scope.clearCheckStatusTimeout();
        $scope.setLoading(true);
        worldpayDriverService.sendRecord(
            new WorldpayTransactionRequestRecord(
                WorldpayTransactionType.CHECK_STATUS
            )
        );
    };

    /**
     * @param {number} amount
     *
     * @return {WorldpayTransactionRequestRecord}
     */
    $scope.createTransactionRequestRecord = function createTransactionRequestRecord(amount) {
        let record = new WorldpayTransactionRequestRecord();
        record.transactionReference = $scope.cart.generatePaymentThirdPartyID();

        if (amount < 0) {
            record.transactionType = WorldpayTransactionType.RETURNS;
            record.transactionAmount = Math.abs(amount);
        } else {
            record.transactionType = WorldpayTransactionType.SALE;
            record.transactionAmount = amount;
        }

        if ($scope.isCnp) {
            record.cardholderNotPresent = true;
        }

        return record;
    };

    /**
     * Cleanup after a successful transaction ready for another.
     */
    $scope.cleanup = function cleanup() {
        // Re-enable all the other payment methods in case we've just recovered a partial payment(!)
        $scope.setRestrictToPaymentMethodRef(null);

        $scope.transactionRecord = null;
    };

    $scope.dismissRecoveryModal = function dismissRecoveryModal() {
        cartService.clearTransactionInProgress();

        // Hide the modal asking the user what to do about a recovered transaction. We've been told what to do now.
        if ($scope.recoveryConfirmationModalID) {
            try {
                vueModalFactory.close($scope.recoveryConfirmationModalID);
            } catch (e) {}
            $scope.recoveryConfirmationModalID = null;
        }
    };

    /**
     * Register listener to receive records coming from the terminal.
     */
    worldpayDriverService.clearRecordReceivedListeners();
    worldpayDriverService.onRecordReceived(
        function (record) {
            $scope.handleReceivedRecord(record);
        }
    );

    /**
     * @param {WorldpayTransactionResponseRecord} record
     */
    $scope.handleReceivedRecord = function handleReceivedRecord(record) {
        $scope.clearCheckStatusTimeout();

        switch (record.transactionResult) {
            case WorldpayTransactionResult.APPROVED_ONLINE:
            case WorldpayTransactionResult.APPROVED_OFFLINE:
            case WorldpayTransactionResult.APPROVED_MANUAL_REFERRAL: {
                // Transaction completed.
                const isRefund = record.transactionType === WorldpayTransactionType.RETURNS;
                const payment = record.toPayment(isRefund);
                payment.setPaymentMethod($scope.thisPaymentMethod);
                $scope.addPaymentFromWorldPay(payment);

                // TODO: When every client is sending their receipts to print we can $scope.finishPayment(); as soon as
                // we receive this result as we know IPC will be finished and ready.
                // And the submitOnReady logic can be removed.
                // $scope.checkRemainingBalanceAndAutoFinishPayment();
                break;
            }
            case WorldpayTransactionResult.STATUS_NOT_BUSY:
                $scope.ready = true;
                $scope.cleanup();

                if ($scope.hasBeenBusy) {
                    $scope.dismissRecoveryModal();
                }
                $scope.hasBeenBusy = false;

                if (typeof $scope.onTerminalReady === 'function') {
                    $scope.onTerminalReady();
                }
                if (submitOnReady === true) {
                    submitOnReady = false;
                    $scope.checkRemainingBalanceAndAutoFinishPayment();
                }
                break;

            case WorldpayTransactionResult.STATUS_BUSY:
                $scope.hasBeenBusy = true;
                $scope.ready = false;
                paymentState.setPaymentModalCancelable(false, $scope.thisPaymentMethodRef);

                log.debug('IPC is busy. Checking again in 1 second.');
                $scope.checkStatusTimeout = $timeout(function () {
                    $scope.checkStatus();
                }, 1000);
                break;
        }

        if (record.transactionResult !== WorldpayTransactionResult.STATUS_BUSY) {
            paymentState.clearPaymentModalCancelable();
        }

        $scope.setLoading(false);

        // Check if we're allowed to send things to IPC (if this record is not already the result of that check)
        if (record.transactionResult !== WorldpayTransactionResult.STATUS_BUSY
            && record.transactionResult !== WorldpayTransactionResult.STATUS_NOT_BUSY) {
            log.debug('Non-status result. Checking status in 1 second.');
            $scope.checkStatusTimeout = $timeout(function () {
                $scope.checkStatus();
            }, 1000);
        }
    };

    $scope.addPaymentFromWorldPay = function addPaymentFromWorldPay(payment) {
        $scope.dismissRecoveryModal();

        $scope.addPayment(payment);
        $scope.cleanup();

        submitOnReady = true;
    };

    $scope.recoveryConfirmationModalID = null;

    $scope.$on(EventNames.PAYMENT_METHOD_CHANGED, function (event, newPaymentMethod) {
        const newPaymentMethodRef = newPaymentMethod?.ref;
        if (newPaymentMethodRef === $scope.thisPaymentMethodRef) {
            worldpayDriverService.connect().then(() => {
                const txnInProgress = cartService.getTransactionInProgress();
                if (txnInProgress && txnInProgress.paymentMethodRef === $scope.thisPaymentMethodRef) {
                    // Started with a recovered transaction.
                    // Display a prompt to ask the operator if it had already completed.

                    $scope.transactionRecord = $scope.createTransactionRequestRecord(
                        txnInProgress.amount
                    );

                    dialogService.customConfirm(
                        '',
                        [
                            {
                                label: 'PAYMENT_WORLDPAY.RECOVERED_TXN_WAIT_BTN',
                                color: 'warning',
                                value: 'wait',
                            },
                            {
                                label: 'PAYMENT_WORLDPAY.RECOVERED_TXN_YES_BTN',
                                color: 'success',
                                value: 'yes',
                            },
                        ],
                        {
                            cancelLabel: 'PAYMENT_WORLDPAY.RECOVERED_TXN_NO_BTN',
                            cancelColor: 'danger',
                            customText: $filter('lang')(
                                'PAYMENT_WORLDPAY.RECOVERED_TXN_MESSAGE',
                                {
                                    amount: txnInProgress.amount,
                                }
                            ),
                        },
                        (modalID) => {
                            $scope.recoveryConfirmationModalID = modalID;
                        }
                    )
                        .then((value) => {
                            switch (value) {
                                case 'yes': {
                                    // Completed.
                                    const payment = new Payment();
                                    payment.setTendered(txnInProgress.amount);
                                    payment.setPaymentMethod($scope.thisPaymentMethod);
                                    // thirdPartyID is purposely missing here as we don't know it.
                                    $scope.addPaymentFromWorldPay(payment);
                                    break;
                                }
                            }
                        })
                        .catch(() => {});
                } else if ($scope.autoStartCardTransactions) {
                    $scope.onTerminalReady = function () {
                        $scope.beginFullTransaction();
                        $scope.onTerminalReady = null;
                    };
                }

                // Switching to this payment method.
                $scope.checkStatus();
            });
        } else {
            // Switching away from this payment method.

            // Hide the IPC window
            $scope.hideWindow();
        }
    });

    /**
     * Hide IPC window when the payment modal is closed.
     */
    $scope.$on('payment-modal.closing', function () {
        // Hide the IPC window
        $scope.hideWindow();
    });
};

module.exports = WorldpayPaymentCtrl;
